import React, { useState, useEffect, useContext } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Modal from '../components/Modal';
import { AuthContext } from '../context/AuthContext';
import { getFirmwares, uploadFirmware, deleteFirmware } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash, faMicrochip } from '@fortawesome/free-solid-svg-icons';

const FirmwarePage = () => {
        const { user } = useContext(AuthContext);
        const [firmwares, setFirmwares] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
        const [selectedFirmware, setSelectedFirmware] = useState(null);
        const [file, setFile] = useState(null);
        const [uploading, setUploading] = useState(false);
        const { showToast } = useToast();

        useEffect(() => {
                fetchFirmwares();
        }, []);

        const fetchFirmwares = async () => {
                try {
                        const response = await getFirmwares();
                        if (!response.ok) {
                                throw new Error('Failed to fetch firmwares');
                        }
                        const data = await response.json();
                        setFirmwares(data.data || []);
                } catch (err) {
                        setError(err.message);
                } finally {
                        setLoading(false);
                }
        };

        const handleUploadFirmware = async () => {
                if (!file) {
                        showToast('error', 'Please select a firmware file');
                        return;
                }

                try {
                        setUploading(true);
                        const formData = new FormData();
                        formData.append('file', file);

                        const response = await uploadFirmware(formData);
                        if (!response.ok) {
                                const data = await response.json();
                                throw new Error(data.message || 'Failed to upload firmware');
                        }

                        const data = await response.json();
                        setFirmwares(prev => [...prev, data.data]);
                        setIsUploadModalOpen(false);
                        setFile(null);
                        showToast('success', 'Firmware uploaded successfully');
                } catch (err) {
                        showToast('error', err.message);
                } finally {
                        setUploading(false);
                }
        };

        const handleDeleteFirmware = async () => {
                try {
                        const response = await deleteFirmware(selectedFirmware.id);
                        if (!response.ok) {
                                const data = await response.json();
                                throw new Error(data.error?.message || 'Failed to delete firmware');
                        }

                        setFirmwares(prev => prev.filter(fw => fw.id !== selectedFirmware.id));
                        setIsDeleteModalOpen(false);
                        setSelectedFirmware(null);
                        showToast('success', 'Firmware deleted successfully');
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        const openDeleteModal = (firmware) => {
                setSelectedFirmware(firmware);
                setIsDeleteModalOpen(true);
        };

        if (loading) {
                return (
                        <div className="flex flex-col min-h-screen">
                                <Header />
                                <main className="flex-grow container mx-auto px-4 py-8">
                                        <p>Loading firmwares...</p>
                                </main>
                                <Footer />
                        </div>
                );
        }

        if (error) {
                return (
                        <div className="flex flex-col min-h-screen">
                                <Header />
                                <main className="flex-grow container mx-auto px-4 py-8">
                                        <p className="text-red-500">Error: {error}</p>
                                </main>
                                <Footer />
                        </div>
                );
        }

        return (
                <div className="flex flex-col min-h-screen">
                        <Header />
                        <main className="flex-grow container mx-auto px-4 py-8">
                                <div className="flex justify-between items-center mb-6">
                                        <h1 className="text-2xl font-bold">Firmware Management</h1>
                                        <button
                                                onClick={() => setIsUploadModalOpen(true)}
                                                data-testid="firmware-upload-open"
                                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2"
                                        >
                                                <FontAwesomeIcon icon={faUpload} />
                                                Upload Firmware
                                        </button>
                                </div>

                                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                                        <table className="min-w-full">
                                                <thead className="bg-gray-100 border-b">
                                                        <tr>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                        ID
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                        Version
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                        Hardware
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                        Url
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                        Actions
                                                                </th>
                                                        </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                        {firmwares.length === 0 ? (
                                                                <tr>
                                                                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                                                                <span data-testid="firmware-empty">No firmwares available</span>
                                                                        </td>
                                                                </tr>
                                                        ) : (
                                                                firmwares.map((firmware) => (
                                                                        <tr key={firmware.id} data-testid="firmware-row" data-firmware-version={firmware.version} className="hover:bg-gray-50">
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                                        {firmware.id}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                                        {firmware.version}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                                        {firmware.hardware}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                                        {firmware.url}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                                        <button
                                                                                                onClick={() => openDeleteModal(firmware)}
                                                                                                data-testid="firmware-delete-open"
                                                                                                className="text-red-600 hover:text-red-800 ml-2"
                                                                                                title="Delete firmware"
                                                                                        >
                                                                                                <FontAwesomeIcon icon={faTrash} />
                                                                                        </button>
                                                                                </td>
                                                                        </tr>
                                                                ))
                                                        )}
                                                </tbody>
                                        </table>
                                </div>
                        </main>
                        <Footer />

                        {/* Upload Modal */}
                        <Modal
                                isOpen={isUploadModalOpen}
                                onClose={() => {
                                        setIsUploadModalOpen(false);
                                        setFile(null);
                                }}
                                title="Upload Firmware"
                        >
                                <div className="space-y-4">
                                        <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Firmware File
                                                </label>
                                                <p className="text-xs text-gray-500 mb-2">
                                                        Filename format: fw_&#123;version&#125;_hw_&#123;hardware&#125;_fota.bin
                                                </p>
                                                <input
                                                        type="file"
                                                        data-testid="firmware-file"
                                                        onChange={(e) => setFile(e.target.files[0])}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        accept=".bin"
                                                />
                                        </div>
                                        <div className="flex justify-end gap-2 mt-6">
                                                <button
                                                        onClick={() => {
                                                                setIsUploadModalOpen(false);
                                                                setFile(null);
                                                        }}
                                                        className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                                        disabled={uploading}
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        onClick={handleUploadFirmware}
                                                        data-testid="firmware-upload-submit"
                                                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400"
                                                        disabled={uploading}
                                                >
                                                        {uploading ? 'Uploading...' : 'Upload'}
                                                </button>
                                        </div>
                                </div>
                        </Modal>

                        {/* Delete Confirmation Modal */}
                        <Modal
                                isOpen={isDeleteModalOpen}
                                onClose={() => {
                                        setIsDeleteModalOpen(false);
                                        setSelectedFirmware(null);
                                }}
                                title="Confirm Delete"
                        >
                                <p className="mb-4">
                                        Are you sure you want to delete firmware version {selectedFirmware?.version}?
                                        This action cannot be undone.
                                </p>
                                <div className="flex justify-end gap-2">
                                        <button
                                                onClick={() => {
                                                        setIsDeleteModalOpen(false);
                                                        setSelectedFirmware(null);
                                                }}
                                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                        >
                                                Cancel
                                        </button>
                                        <button
                                                onClick={handleDeleteFirmware}
                                                data-testid="firmware-delete-confirm"
                                                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                        >
                                                Delete
                                        </button>
                                </div>
                        </Modal>
                </div>
        );
};

export default FirmwarePage;
